import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import {
  Box,
  Heading,
  Text,
  HStack,
  Spinner,
  Flex,
  Card,
  CardBody,
  useColorModeValue,
  Icon,
  Button,
  Badge,
  IconButton,
  Tooltip,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Image,
  Skeleton,
  Tabs,
  TabList,
  Tab,
  SimpleGrid,
} from '@chakra-ui/react';
import { useCustomToast } from '../../utils/customToast';
import { 
  FaBook,
  FaBookOpen,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaBookmark,
  FaRegBookmark,
  FaThumbsUp,
  FaThumbsDown,
  FaEllipsisV,
  FaFilter,
  FaStar,
  FaNewspaper,
  FaGlobe,
  FaMapMarkerAlt,
  FaIndustry,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import { getReadingItems, updateReadingItem, markReadingItemAsRead, fetchReadingItemPreview } from '../../services/playbookService';
import { logger } from '../../utils/logger';
import { SectionLabel } from '../dashboards/DashboardWrapper';

// Source icon mapping (moved outside component to prevent recreation)
const getSourceIcon = (source) => {
  const sourceLower = source?.toLowerCase() || '';
  if (sourceLower.includes('forbes')) return FaNewspaper;
  if (sourceLower.includes('inc')) return FaNewspaper;
  if (sourceLower.includes('harvard')) return FaBook;
  if (sourceLower.includes('entrepreneur')) return FaNewspaper;
  return FaGlobe;
};

// Relevance score colors (moved outside component to prevent recreation)
const getRelevanceColor = (score) => {
  if (score >= 4) return 'green';
  if (score >= 3) return 'yellow';
  return 'gray';
};

// CoverFlow Card Component - moved OUTSIDE PlaybookReadingPage to prevent
// React from recreating every card instance on each selectedIndex change.
// Wrapped with memo() so cards only re-render when their own props change.
const CoverFlowCard = memo(({
  item,
  offset,
  isDragging,
  previewImage,
  onOpenLink,
  onFeedback,
  onMarkRead,
  onSelect,
  cardBg,
  borderColor,
  subtleTextColor,
  primaryColor,
  linkColor,
  fallbackBg,
  hoverBg,
  selectedBorderColor,
}) => {
  const isRead = !!item.read_at;
  const isSaved = item.user_feedback === 'saved';
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Calculate iTunes-style 3D transforms
  const absOffset = Math.abs(offset);
  const direction = offset > 0 ? 1 : -1;
  
  // Center card
  const isCenter = offset === 0;
  const rotateY = isCenter ? 0 : direction * 55;
  const translateX = isCenter ? 0 : direction * (240 + (absOffset - 1) * 90);
  const translateZ = isCenter ? 100 : -50;
  const scale = isCenter ? 1 : 0.75;
  const cardOpacity = isCenter ? 1 : Math.max(0.4, 1 - absOffset * 0.15);

  return (
    <Box
      position="absolute"
      left="50%"
      top="50%"
      w={{ base: '300px', md: '360px', lg: '400px' }}
      h={{ base: '400px', md: '460px', lg: '500px' }}
      cursor={isCenter ? 'default' : 'pointer'}
      onClick={!isCenter ? onSelect : undefined}
      transition={isDragging ? 'none' : 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'}
      style={{
        transform: `translateX(calc(-50% + ${translateX}px)) translateY(-50%) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
        zIndex: isCenter ? 100 : 50 - absOffset,
        opacity: cardOpacity,
        transformStyle: 'preserve-3d',
        transformOrigin: isCenter ? 'center center' : (direction > 0 ? 'left center' : 'right center'),
      }}
      _hover={!isCenter ? { opacity: Math.min(1, cardOpacity + 0.2) } : undefined}
    >
      <Card
        bg={cardBg}
        borderWidth={isCenter ? '3px' : '1px'}
        borderColor={isCenter ? selectedBorderColor : borderColor}
        borderRadius="xl"
        overflow="hidden"
        h="100%"
        boxShadow={isCenter 
          ? '0 30px 60px -20px rgba(0,0,0,0.5), 0 0 40px -10px rgba(59,130,246,0.3)'
          : '0 15px 35px -10px rgba(0,0,0,0.4)'
        }
        onMouseDown={isCenter ? (e) => e.stopPropagation() : undefined}
        onTouchStart={isCenter ? (e) => e.stopPropagation() : undefined}
      >
        {/* Preview Image */}
        <Box position="relative" h="55%" overflow="hidden" bg="gray.100">
          {previewImage && !imageError ? (
            <>
              {!imageLoaded && (
                <Skeleton position="absolute" top={0} left={0} w="100%" h="100%" />
              )}
              <Image
                src={previewImage}
                alt={item.title}
                w="100%"
                h="100%"
                objectFit="cover"
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                opacity={imageLoaded ? 1 : 0}
                transition="opacity 0.3s"
              />
            </>
          ) : (
            <Flex w="100%" h="100%" bg={fallbackBg} justify="center" align="center">
              <Icon as={getSourceIcon(item.source)} boxSize={14} color={primaryColor} opacity={0.6} />
            </Flex>
          )}
          {/* Source badge */}
          <Badge 
            position="absolute" 
            top={3} 
            left={3} 
            bg="blackAlpha.700"
            color="white"
            fontSize="xs"
            px={2}
            py={1}
          >
            {item.source}
          </Badge>
          {/* Relevance score */}
          <Badge 
            position="absolute" 
            top={3} 
            right={3} 
            colorScheme={getRelevanceColor(item.relevance_score)} 
            fontSize="xs"
          >
            <Flex align="center" gap={1}>
              <Icon as={FaStar} boxSize={2.5} />
              <Text as="span">{item.relevance_score}</Text>
            </Flex>
          </Badge>
        </Box>

        {/* Content */}
        <CardBody flex={1} display="flex" flexDirection="column" p={4}>
          <Text
            fontWeight="bold"
            fontSize={isCenter ? 'lg' : 'md'}
            color={linkColor}
            noOfLines={2}
            lineHeight="short"
            mb={2}
            cursor={isCenter ? 'pointer' : 'default'}
            onClick={isCenter ? () => onOpenLink(item) : undefined}
            _hover={isCenter ? { color: 'blue.600' } : undefined}
          >
            {item.title}
          </Text>

          {/* Status badges */}
          <HStack spacing={2} mb={2} flexWrap="wrap">
            {isRead && (
              <Badge colorScheme="green" variant="subtle" fontSize="xs">
                <Flex align="center" gap={1}>
                  <Icon as={FaCheckCircle} boxSize={2} />
                  <Text as="span">Read</Text>
                </Flex>
              </Badge>
            )}
            {isSaved && (
              <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                <Flex align="center" gap={1}>
                  <Icon as={FaBookmark} boxSize={2} />
                  <Text as="span">Saved</Text>
                </Flex>
              </Badge>
            )}
          </HStack>

          {/* Description - only for center card */}
          {isCenter && (
            <Text color={subtleTextColor} fontSize="sm" noOfLines={2} mb={3}>
              {item.relevance_reason}
            </Text>
          )}

          {/* Actions - only for center card */}
          {isCenter && (
            <Box mt="auto" pt={2}>
              <HStack justify="space-between">
                <Text fontSize="xs" color={subtleTextColor}>
                  {new Date(item.created_at).toLocaleDateString()}
                </Text>
                <HStack spacing={2}>
                  <Tooltip label={isSaved ? "Unsave" : "Save"}>
                    <IconButton
                      icon={<Icon as={isSaved ? FaBookmark : FaRegBookmark} />}
                      variant="ghost"
                      size="sm"
                      color={isSaved ? 'purple.500' : 'gray.400'}
                      onClick={(e) => {
                        e.stopPropagation();
                        onFeedback(item.reading_id, isSaved ? null : 'saved');
                      }}
                      aria-label="Save"
                    />
                  </Tooltip>
                  <Menu isLazy>
                    <MenuButton
                      as={IconButton}
                      icon={<Icon as={FaEllipsisV} />}
                      variant="ghost"
                      size="sm"
                      color="gray.400"
                      aria-label="More"
                      _hover={{ bg: hoverBg }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Portal>
                      <MenuList zIndex={1500}>
                        {!isRead && (
                          <MenuItem
                            icon={<Icon as={FaCheckCircle} color="green.500" />}
                            onClick={() => onMarkRead(item.reading_id)}
                          >
                            Mark as Read
                          </MenuItem>
                        )}
                        <MenuItem
                          icon={<Icon as={FaThumbsUp} color="green.500" />}
                          onClick={() => onFeedback(item.reading_id, 'helpful')}
                        >
                          Helpful
                        </MenuItem>
                        <MenuItem
                          icon={<Icon as={FaThumbsDown} color="red.500" />}
                          onClick={() => onFeedback(item.reading_id, 'not_helpful')}
                        >
                          Not Helpful
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<Icon as={FaExternalLinkAlt} boxSize={3} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenLink(item);
                    }}
                  >
                    Read
                  </Button>
                </HStack>
              </HStack>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Reflection - only for center */}
      {isCenter && (
        <Box
          position="absolute"
          bottom="-100px"
          left="5%"
          right="5%"
          h="100px"
          bgGradient="linear(to-b, blackAlpha.300, transparent)"
          transform="scaleY(-1) rotateX(180deg)"
          borderRadius="xl"
          pointerEvents="none"
          opacity={0.4}
        />
      )}
    </Box>
  );
});

CoverFlowCard.displayName = 'CoverFlowCard';

const PlaybookReadingPage = ({ sectionLabel, demoMode, demoData } = {}) => {
  const [readingItems, setReadingItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [readFilter, setReadFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('location');
  const [isUpdating, setIsUpdating] = useState(false);
  const [previewImages, setPreviewImages] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const fetchedPreviewsRef = useRef(new Set());
  const carouselRef = useRef(null);
  const hasSetInitialIndex = useRef(false);

  const toast = useCustomToast();

  // Theme colors - Black and Blue theme
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const primaryColor = useColorModeValue('blue.600', 'blue.400');
  const linkColor = useColorModeValue('gray.900', 'white');
  const carouselBg = useColorModeValue('linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)', 'linear-gradient(180deg, #1a202c 0%, #2d3748 100%)');
  const fallbackBg = useColorModeValue('blue.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBorderColor = useColorModeValue('blue.400', 'blue.300');

  // Fetch reading items once on mount (no scope filter - fetch all)
  const fetchReadingItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const itemsData = await getReadingItems();
      setReadingItems(itemsData || []);
      
      // Default to 'all' tab to show all reading items
      setScopeFilter('all');
      
      logger.info('PlaybookReadingPage: Loaded reading items', { count: itemsData?.length });
    } catch (error) {
      logger.error('PlaybookReadingPage: Error loading reading items', error);
      toast({
        title: 'Error loading reading list',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setReadingItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (demoMode && demoData) {
      setReadingItems(demoData.readingItems || []);
      setIsLoading(false);
      return;
    }
    fetchReadingItems();
  }, [demoMode, demoData, fetchReadingItems]);

  // Fetch preview images for reading items that don't have one cached
  useEffect(() => {
    if (!readingItems.length) return;
    
    const fetchPreviews = async () => {
      const itemsNeedingPreview = readingItems.filter(
        item => !item.preview_image && 
                !fetchedPreviewsRef.current.has(item.reading_id)
      );
      
      if (!itemsNeedingPreview.length) return;
      
      // Mark items as being fetched before starting
      itemsNeedingPreview.forEach(item => {
        fetchedPreviewsRef.current.add(item.reading_id);
      });
      
      // Fetch previews in parallel (limited to 5 at a time)
      const batchSize = 5;
      for (let i = 0; i < itemsNeedingPreview.length; i += batchSize) {
        const batch = itemsNeedingPreview.slice(i, i + batchSize);
        const results = await Promise.all(
          batch.map(item => 
            fetchReadingItemPreview(item.reading_id).catch(() => ({ 
              reading_id: item.reading_id, 
              preview_image: null 
            }))
          )
        );
        
        // Update preview images state
        const newPreviews = {};
        results.forEach(result => {
          if (result.preview_image) {
            newPreviews[result.reading_id] = result.preview_image;
          }
        });
        
        if (Object.keys(newPreviews).length > 0) {
          setPreviewImages(prev => ({ ...prev, ...newPreviews }));
        }
      }
    };
    
    fetchPreviews();
  }, [readingItems]);

  // Navigate to previous item
  const goToPrevious = useCallback(() => {
    setSelectedIndex(prev => Math.max(0, prev - 1));
  }, []);

  // Navigate to next item
  const goToNext = useCallback((maxIndex) => {
    setSelectedIndex(prev => Math.min(maxIndex, prev + 1));
  }, []);


  // Mouse/Touch drag handlers
  const handleDragStart = useCallback((e) => {
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    setDragStartX(clientX);
    setDragOffset(0);
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const offset = clientX - dragStartX;
    setDragOffset(offset);
  }, [isDragging, dragStartX]);

  const handleDragEnd = useCallback((filteredItemsLength) => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const threshold = 50; // pixels
    if (dragOffset > threshold) {
      goToPrevious();
    } else if (dragOffset < -threshold) {
      setSelectedIndex(prev => Math.min(filteredItemsLength - 1, prev + 1));
    }
    setDragOffset(0);
  }, [isDragging, dragOffset, goToPrevious]);

  // Handle mark as read - update local state instead of refetching
  const handleMarkRead = useCallback(async (readingId) => {
    setIsUpdating(true);
    try {
      await markReadingItemAsRead(readingId);
      // Update local state to avoid full reload blink
      setReadingItems(prev => prev.map(i => 
        i.reading_id === readingId 
          ? { ...i, read_at: new Date().toISOString() }
          : i
      ));
      toast({
        title: 'Marked as read',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      logger.error('PlaybookReadingPage: Error marking as read', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  }, [toast]);

  // Handle feedback - update local state instead of refetching
  const handleFeedback = useCallback(async (readingId, feedback) => {
    setIsUpdating(true);
    try {
      await updateReadingItem(readingId, { user_feedback: feedback });
      // Update local state to avoid full reload blink
      setReadingItems(prev => prev.map(i => 
        i.reading_id === readingId 
          ? { ...i, user_feedback: feedback }
          : i
      ));
      toast({
        title: feedback === 'saved' ? 'Saved!' : 'Feedback recorded',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      logger.error('PlaybookReadingPage: Error updating feedback', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  }, [toast]);

  // Open link and mark as read - update local state instead of refetching
  const handleOpenLink = useCallback((item) => {
    if (!item.read_at) {
      markReadingItemAsRead(item.reading_id).catch(() => {});
      // Update local state to avoid full reload blink
      setReadingItems(prev => prev.map(i => 
        i.reading_id === item.reading_id 
          ? { ...i, read_at: new Date().toISOString() }
          : i
      ));
    }
    window.open(item.url, '_blank', 'noopener,noreferrer');
  }, []);

  // Filter reading items by scope and read status (client-side)
  const filteredItems = useMemo(() => {
    return readingItems.filter(item => {
      // Allow business, location, and industry scoped items
      if (!['business', 'location', 'industry'].includes(item.scope)) return false;
      // Scope filter - 'all' shows everything, otherwise filter by specific scope
      if (scopeFilter !== 'all' && item.scope !== scopeFilter) return false;
      // Read status filter
      if (readFilter === 'unread') return !item.read_at;
      if (readFilter === 'read') return !!item.read_at;
      if (readFilter === 'saved') return item.user_feedback === 'saved';
      return true;
    });
  }, [readingItems, scopeFilter, readFilter]);

  // Jump to the middle of the list on initial load
  useEffect(() => {
    if (filteredItems.length > 0 && !hasSetInitialIndex.current) {
      setSelectedIndex(Math.floor(filteredItems.length / 2));
      hasSetInitialIndex.current = true;
    }
  }, [filteredItems.length]);

  // Reset selectedIndex when filters change or when it exceeds filtered items
  useEffect(() => {
    if (selectedIndex >= filteredItems.length && filteredItems.length > 0) {
      setSelectedIndex(filteredItems.length - 1);
    } else if (filteredItems.length === 0) {
      setSelectedIndex(0);
    }
  }, [filteredItems.length, selectedIndex]);

  // Keyboard navigation with filtered items length
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(filteredItems.length - 1, prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredItems.length]);

  // Loading state
  if (isLoading) {
    return (
      <Box w="100%" py={4} px={{ base: 2, md: 4 }}>
        {/* Section Label */}
        <SectionLabel description="Curated articles from around the web that are relevant to your industry. Stay informed with the latest insights and best practices.">
          Playbook: Reading List
        </SectionLabel>

        <Flex justify="center" align="center" minH="500px" w="100%">
          <Spinner size="xl" thickness="4px" speed="0.65s" emptyColor="gray.200" color={primaryColor} />
          <Text ml={4} fontSize="lg" color="gray.500">Loading Reading List...</Text>
        </Flex>
      </Box>
    );
  }

  // Empty state
  if (!readingItems.length) {
    return (
      <Box w="100%" py={4} px={{ base: 2, md: 4 }}>
        {/* Section Label */}
        <SectionLabel description="Curated articles from around the web that are relevant to your industry. Stay informed with the latest insights and best practices.">
          Playbook: Reading List
        </SectionLabel>

        <Flex
          direction="column"
          align="center"
          justify="center"
          p={10}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          bg={cardBg}
          minH="300px"
          boxShadow="md"
        >
          <Icon as={FaBookOpen} boxSize={16} color="gray.400" mb={6} />
          <Heading size="lg" color="gray.600" mb={3}>No Reading Items Available</Heading>
          <Text color="gray.500" textAlign="center" maxW="500px">
            Industry articles from around the web will appear here as we find content relevant to your market.
          </Text>
        </Flex>
      </Box>
    );
  }

  // Summary stats for header - count business, local, and industry scoped items
  const availableItems = readingItems.filter(item => ['business', 'location', 'industry'].includes(item.scope));
  const stats = {
    total: availableItems.length,
    unread: availableItems.filter(item => !item.read_at).length,
    read: availableItems.filter(item => item.read_at).length,
    saved: availableItems.filter(item => item.user_feedback === 'saved').length,
  };

  return (
    <Box w="100%" py={4} px={{ base: 2, md: 4 }}>
      {/* Section Label */}
      <SectionLabel description="Curated articles from around the web that are relevant to your industry. Stay informed with the latest insights and best practices.">
        Playbook: Reading List
      </SectionLabel>

      {/* Filters and Tabs Row */}
      <Flex 
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
        gap={4}
        mb={6}
      >
        {/* Scope Tabs */}
        <Tabs
          variant="soft-rounded"
          colorScheme="blue"
          size="sm"
          index={['all', 'business', 'location', 'industry'].indexOf(scopeFilter)}
          onChange={(index) => setScopeFilter(['all', 'business', 'location', 'industry'][index])}
        >
          <TabList flexWrap="wrap" gap={2}>
            <Tab fontSize="sm" px={4}>
              <Icon as={FaBook} mr={2} boxSize={3} />
              All
            </Tab>
            <Tab fontSize="sm" px={4}>
              <Icon as={FaStar} mr={2} boxSize={3} />
              Business
            </Tab>
            <Tab fontSize="sm" px={4}>
              <Icon as={FaMapMarkerAlt} mr={2} boxSize={3} />
              Local
            </Tab>
            <Tab fontSize="sm" px={4}>
              <Icon as={FaIndustry} mr={2} boxSize={3} />
              Industry
            </Tab>
          </TabList>
        </Tabs>

        {/* Filters */}
        <HStack spacing={3} flexWrap="wrap">
          <Flex align="center" gap={2}>
            <Icon as={FaFilter} color={subtleTextColor} boxSize={3} />
            <Text as="span" fontSize="sm" color={subtleTextColor}>Filter:</Text>
          </Flex>
          <Select
            size="sm"
            w="140px"
            value={readFilter}
            onChange={(e) => setReadFilter(e.target.value)}
            bg={cardBg}
            borderRadius="md"
          >
            <option value="all">All Items</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
            <option value="saved">Saved</option>
          </Select>
        </HStack>
      </Flex>

      {/* CoverFlow Carousel */}
      {filteredItems.length > 0 ? (
        <Box mb={8}>
          {/* Carousel Container */}
          <Box
            ref={carouselRef}
            position="relative"
            h={{ base: '480px', md: '540px', lg: '580px' }}
            overflow="hidden"
            borderRadius="2xl"
            bg={carouselBg}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={() => handleDragEnd(filteredItems.length)}
            onMouseLeave={() => handleDragEnd(filteredItems.length)}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={() => handleDragEnd(filteredItems.length)}
            cursor={isDragging ? 'grabbing' : 'grab'}
            userSelect="none"
            style={{
              perspective: '1200px',
              perspectiveOrigin: '50% 50%',
            }}
          >
            {/* 3D Stage */}
            <Box
              position="relative"
              w="100%"
              h="100%"
              style={{
                transformStyle: 'preserve-3d',
              }}
            >
              {/* Render cards - show 4 on each side of selected */}
              {filteredItems.map((item, index) => {
                const offset = index - selectedIndex;
                if (Math.abs(offset) > 4) return null;
                
                return (
                  <CoverFlowCard
                    key={item.reading_id}
                    item={item}
                    offset={offset}
                    isDragging={isDragging}
                    previewImage={item.preview_image || previewImages[item.reading_id]}
                    onOpenLink={handleOpenLink}
                    onFeedback={handleFeedback}
                    onMarkRead={handleMarkRead}
                    onSelect={() => setSelectedIndex(index)}
                    cardBg={cardBg}
                    borderColor={borderColor}
                    subtleTextColor={subtleTextColor}
                    primaryColor={primaryColor}
                    linkColor={linkColor}
                    fallbackBg={fallbackBg}
                    hoverBg={hoverBg}
                    selectedBorderColor={selectedBorderColor}
                  />
                );
              })}
            </Box>

            {/* Navigation Arrows */}
            <IconButton
              icon={<Icon as={FaChevronLeft} boxSize={6} />}
              position="absolute"
              left={4}
              top="50%"
              transform="translateY(-50%)"
              zIndex={200}
              size="lg"
              variant="solid"
              colorScheme="blue"
              borderRadius="full"
              isDisabled={selectedIndex === 0}
              onClick={goToPrevious}
              aria-label="Previous article"
              boxShadow="lg"
              _hover={{ transform: 'translateY(-50%) scale(1.1)' }}
              opacity={selectedIndex === 0 ? 0.3 : 0.9}
            />
            <IconButton
              icon={<Icon as={FaChevronRight} boxSize={6} />}
              position="absolute"
              right={4}
              top="50%"
              transform="translateY(-50%)"
              zIndex={200}
              size="lg"
              variant="solid"
              colorScheme="blue"
              borderRadius="full"
              isDisabled={selectedIndex === filteredItems.length - 1}
              onClick={() => goToNext(filteredItems.length - 1)}
              aria-label="Next article"
              boxShadow="lg"
              _hover={{ transform: 'translateY(-50%) scale(1.1)' }}
              opacity={selectedIndex === filteredItems.length - 1 ? 0.3 : 0.9}
            />

            {/* Bottom Gradient Fade */}
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              h="80px"
              bgGradient="linear(to-t, blackAlpha.400, transparent)"
              pointerEvents="none"
              borderBottomRadius="2xl"
            />
          </Box>

          {/* Position Indicator */}
          <Flex justify="center" mt={4} gap={2}>
            {filteredItems.map((_, index) => (
              <Box
                key={index}
                w={index === selectedIndex ? '24px' : '8px'}
                h="8px"
                borderRadius="full"
                bg={index === selectedIndex ? 'blue.500' : 'gray.300'}
                cursor="pointer"
                onClick={() => setSelectedIndex(index)}
                transition="all 0.3s ease"
                _hover={{ bg: index === selectedIndex ? 'blue.600' : 'gray.400' }}
              />
            ))}
          </Flex>

          {/* Keyboard hint */}
          <Text textAlign="center" fontSize="sm" color={subtleTextColor} mt={3}>
            Use <Badge variant="subtle" colorScheme="gray" mx={1}>←</Badge> 
            <Badge variant="subtle" colorScheme="gray" mx={1}>→</Badge> arrow keys or swipe to navigate
          </Text>
        </Box>
      ) : (
        <Flex
          direction="column"
          align="center"
          justify="center"
          p={10}
          bg={cardBg}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={borderColor}
          minH="300px"
        >
          <Icon as={FaFilter} boxSize={12} color="gray.300" mb={4} />
          <Text color={subtleTextColor} fontSize="lg" mb={2}>No items match your filters</Text>
          <Button
            variant="outline"
            colorScheme="blue"
            size="sm"
            onClick={() => {
              setReadFilter('all');
              setScopeFilter('all');
            }}
          >
            Clear all filters
          </Button>
        </Flex>
      )}

      {/* Stats Section */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mt={6}>
        <Box 
          bg={cardBg} 
          p={4} 
          borderRadius="lg" 
          borderWidth="1px" 
          borderColor={borderColor}
          textAlign="center"
        >
          <Text fontSize="2xl" fontWeight="bold" color={primaryColor}>{stats.total}</Text>
          <Text fontSize="sm" color={subtleTextColor}>Total Articles</Text>
        </Box>
        <Box 
          bg={cardBg} 
          p={4} 
          borderRadius="lg" 
          borderWidth="1px" 
          borderColor="blue.200"
          textAlign="center"
        >
          <Text fontSize="2xl" fontWeight="bold" color="blue.500">{stats.unread}</Text>
          <Text fontSize="sm" color={subtleTextColor}>Unread</Text>
        </Box>
        <Box 
          bg={cardBg} 
          p={4} 
          borderRadius="lg" 
          borderWidth="1px" 
          borderColor="gray.300"
          textAlign="center"
        >
          <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats.read}</Text>
          <Text fontSize="sm" color={subtleTextColor}>Read</Text>
        </Box>
        <Box 
          bg={cardBg} 
          p={4} 
          borderRadius="lg" 
          borderWidth="1px" 
          borderColor="blue.300"
          textAlign="center"
        >
          <Text fontSize="2xl" fontWeight="bold" color="blue.600">{stats.saved}</Text>
          <Text fontSize="sm" color={subtleTextColor}>Saved</Text>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default PlaybookReadingPage;

